/**
 * Extracts a tenant/company subdomain from `hostname`, given the app's own base domain for
 * the current environment (e.g. "app-mobile-staging.prizmwms.com", configured per
 * environment as `appBaseDomain`). This is entirely config-driven — no fixed dot-count
 * heuristic — so it works for any domain shape a customer's deployment might use (a
 * different TLD entirely, a base domain with multiple labels, etc.) as long as
 * `appBaseDomain` is set correctly for that environment.
 *
 * Returns null when:
 *  - `appBaseDomain` is blank (subdomain resolution disabled for this environment, e.g. the
 *    local `development` config, which relies on the separate `devTenantCode` bypass instead),
 *  - `hostname` is an exact match for `appBaseDomain` (bare host, no subdomain),
 *  - `hostname` does not end with `.{appBaseDomain}`,
 *  - the extracted subdomain is exactly "www".
 */
export function extractSubdomain(hostname: string, appBaseDomain: string): string | null {
  if (!appBaseDomain || !hostname) return null;
  if (hostname === appBaseDomain) return null;

  const suffix = `.${appBaseDomain}`;
  if (!hostname.endsWith(suffix)) return null;

  const subdomain = hostname.slice(0, -suffix.length);
  return subdomain && subdomain !== 'www' ? subdomain : null;
}

/**
 * Whether `hostname` lives on the app's own base domain for this environment — either the
 * bare apex host or any subdomain of it. Used to decide whether a hard-redirect to a tenant's
 * own subdomain is possible (see `buildTenantUrl`): on localhost dev, `appBaseDomain` is blank
 * and this always returns false, which is the signal to fall back to in-place navigation
 * instead (see the `devTenantCode` bypass in `TenantService`).
 */
export function isOnAppBaseDomain(hostname: string, appBaseDomain: string): boolean {
  if (!appBaseDomain || !hostname) return false;
  return hostname === appBaseDomain || hostname.endsWith(`.${appBaseDomain}`);
}

/**
 * Builds the absolute URL for a tenant's own subdomain on `appBaseDomain`, preserving the
 * current protocol and port. Always points at the root path — `LaunchPage` re-resolves the
 * URL on every activation (see its `ionViewWillEnter`) and routes to `/login` itself, so a
 * full navigation to `/` is enough to land the user back where they'd expect.
 */
export function buildTenantUrl(
  tenancyName: string,
  appBaseDomain: string,
  location: Pick<Location, 'protocol' | 'port'>,
): string {
  const portSuffix = location.port ? `:${location.port}` : '';
  return `${location.protocol}//${tenancyName}.${appBaseDomain}${portSuffix}/`;
}
